import React, { useEffect } from 'react';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    gaBlocked?: boolean;
  }
}

interface GoogleAnalyticsProps {
  measurementId: string;
}

const GoogleAnalytics: React.FC<GoogleAnalyticsProps> = ({ measurementId }) => {
  useEffect(() => {
    // Track page view on component mount
    try {
      if (window.gtag && !window.gaBlocked) {
        window.gtag('config', measurementId, {
          page_path: window.location.pathname + window.location.search,
        });
      }
    } catch (e) {
      console.warn('Analytics blocked or failed to initialize', e);
    }
  }, [measurementId]);

  // Track page views when route changes
  useEffect(() => {
    const handleRouteChange = () => {
      try {
        if (window.gtag && !window.gaBlocked) {
          window.gtag('config', measurementId, {
            page_path: window.location.pathname + window.location.search,
          });
        }
      } catch (e) {
        // Silently fail for route changes if blocked
      }
    };

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleRouteChange);

    // Custom event listener for route changes
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      handleRouteChange();
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      handleRouteChange();
    };

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [measurementId]);

  return null; // This component doesn't render anything
};

// Utility functions for tracking custom events
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  try {
    if (window.gtag && !window.gaBlocked) {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }
  } catch (e) {
    // Fail silently
  }
};

// Track form submissions
export const trackFormSubmission = (formName: string) => {
  try {
    trackEvent('form_submit', 'engagement', formName);

    // Enhanced tracking for lead generation
    if (window.gtag && !window.gaBlocked) {
      window.gtag('event', 'generate_lead', {
        event_category: 'lead_generation',
        event_label: formName,
        value: 1
      });
    }
  } catch (e) {
    // Fail silently
  }
};

// Track button clicks
export const trackButtonClick = (buttonName: string, location?: string) => {
  trackEvent('button_click', 'engagement', `${buttonName}${location ? `_${location}` : ''}`);
};

// Track page views manually
export const trackPageView = (pagePath: string) => {
  try {
    if (window.gtag && !window.gaBlocked) {
      window.gtag('config', 'G-XLMPWHRHEB', {
        page_path: pagePath,
      });
    }
  } catch (e) {
    // Fail silently
  }
};

// Track scroll depth
export const trackScrollDepth = (depth: number) => {
  trackEvent('scroll', 'engagement', 'scroll_depth', depth);
};

// Track time on page
export const trackTimeOnPage = (seconds: number) => {
  trackEvent('timing_complete', 'engagement', 'time_on_page', seconds);
};

// Enhanced conversion tracking
export const trackConversion = (conversionType: string, value?: number) => {
  try {
    if (window.gtag && !window.gaBlocked) {
      window.gtag('event', 'conversion', {
        event_category: 'conversion',
        event_label: conversionType,
        value: value || 1
      });
    }
  } catch (e) {
    // Fail silently
  }
};

// Track service views
export const trackServiceView = (serviceName: string, serviceCategory: string) => {
  try {
    if (window.gtag && !window.gaBlocked) {
      window.gtag('event', 'view_item', {
        items: [{
          item_id: serviceName.toLowerCase().replace(/\s+/g, '_'),
          item_name: serviceName,
          item_category: serviceCategory,
          item_category2: 'services'
        }]
      });
    }
  } catch (e) {
    // Fail silently
  }
};

// Track quote requests
export const trackQuoteRequest = (serviceType: string) => {
  try {
    if (window.gtag && !window.gaBlocked) {
      window.gtag('event', 'begin_checkout', {
        items: [{
          item_id: 'quote_request',
          item_name: `${serviceType} Quote Request`,
          item_category: 'lead_generation',
          item_category2: 'quote'
        }]
      });

      // Also track as lead generation
      trackEvent('generate_lead', 'lead_generation', `quote_${serviceType.toLowerCase().replace(/\s+/g, '_')}`);
    }
  } catch (e) {
    // Fail silently
  }
};

// Track phone calls
export const trackPhoneCall = (phoneNumber: string, location?: string) => {
  if (window.gtag) {
    window.gtag('event', 'phone_call', {
      event_category: 'lead_generation',
      event_label: `phone_call_${location || 'general'}`,
      phone_number: phoneNumber
    });
  }
};

// Track email clicks
export const trackEmailClick = (emailType: string) => {
  trackEvent('email_click', 'engagement', emailType);
};

// Track social media engagement
export const trackSocialEngagement = (platform: string, action: string) => {
  trackEvent('social_engagement', 'engagement', `${platform}_${action}`);
};

// Track video interactions
export const trackVideoInteraction = (videoName: string, action: string) => {
  trackEvent('video_interaction', 'engagement', `${videoName}_${action}`);
};

// Track file downloads
export const trackFileDownload = (fileName: string, fileType: string) => {
  trackEvent('file_download', 'engagement', `${fileName}_${fileType}`);
};

// Enhanced scroll tracking with thresholds
export const trackScrollWithThresholds = () => {
  let scrollThresholds = [25, 50, 75, 90];
  let trackedThresholds: number[] = [];

  const handleScroll = () => {
    const scrollPercent = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100);

    scrollThresholds.forEach(threshold => {
      if (scrollPercent >= threshold && !trackedThresholds.includes(threshold)) {
        trackScrollDepth(threshold);
        trackedThresholds.push(threshold);
      }
    });
  };

  window.addEventListener('scroll', handleScroll);

  // Cleanup function
  return () => window.removeEventListener('scroll', handleScroll);
};

// Track user engagement score
export const trackEngagementScore = (score: number, factors: string[]) => {
  if (window.gtag) {
    window.gtag('event', 'engagement_score', {
      event_category: 'engagement',
      event_label: factors.join('_'),
      value: score
    });
  }
};

export default GoogleAnalytics;
